import { useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { campaignJobBoardQueryKeys } from "./queryKeys";

import { campaignQueryKeys } from "@/features/campaigns/api/queryKeys";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { integrationsKeys } from "@/features/integrations/hooks/useIntegrations";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { EditCampaignJobBoardRequest } from "@/types/request/EditCampaignJobBoardRequest";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

interface UseEditCampaignJobBoardMutationParams {
  campaignId: string;
  clientId?: string;
  clientIntegrationModuleId: string;
}

type EditCampaignJobBoardContext = {
  previousDetail?: CampaignJobBoardType;
  previousList?: PagedResponse<CampaignJobBoardType>;
  previousInfinite?: InfiniteData<PagedResponse<CampaignJobBoardType>>;
};

function mergeCampaignJobBoard(
  original: CampaignJobBoardType | undefined,
  payload: EditCampaignJobBoardRequest
): CampaignJobBoardType | undefined {
  if (!original) {
    return original;
  }

  return {
    ...original,
    endDate: payload.endDate === undefined ? original.endDate : payload.endDate,
    monthlyBudget:
      payload.monthlyBudget === undefined ? original.monthlyBudget : payload.monthlyBudget,
    startDate: payload.startDate,
  };
}

export function useEditCampaignJobBoardMutation({
  campaignId,
  clientId,
  clientIntegrationModuleId,
}: UseEditCampaignJobBoardMutationParams) {
  const queryClient = useQueryClient();

  return useMutation<
    CampaignJobBoardType,
    AxiosError<ValidationErrorResponse>,
    EditCampaignJobBoardRequest,
    EditCampaignJobBoardContext
  >({
    mutationFn: (payload) =>
      instance
        .put(`/api/v2/Campaigns/${campaignId}/Integrations/${clientIntegrationModuleId}`, {
          ...payload,
          campaignId,
          clientId2: clientId,
        })
        .then((response) => response.data),
    onMutate: async (payload) => {
      const detailKey = campaignJobBoardQueryKeys.detail(campaignId, clientIntegrationModuleId, clientId);
      const listKey = campaignJobBoardQueryKeys.list(campaignId, clientId);
      const infiniteKey = campaignJobBoardQueryKeys.infinite(campaignId, clientId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({ queryKey: listKey }),
        queryClient.cancelQueries({ queryKey: infiniteKey }),
      ]);

      const previousDetail = queryClient.getQueryData<CampaignJobBoardType>(detailKey);
      const previousList = queryClient.getQueryData<PagedResponse<CampaignJobBoardType>>(listKey);
      const previousInfinite = queryClient.getQueryData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(infiniteKey);

      const optimisticDetail = mergeCampaignJobBoard(previousDetail, payload);
      if (optimisticDetail) {
        queryClient.setQueryData<CampaignJobBoardType>(detailKey, optimisticDetail);
      }

      if (previousList) {
        queryClient.setQueryData<PagedResponse<CampaignJobBoardType>>(listKey, {
          ...previousList,
          data: previousList.data.map((item) =>
            item.clientIntegrationModuleId === clientIntegrationModuleId
              ? mergeCampaignJobBoard(item, payload) ?? item
              : item
          ),
        });
      }

      if (previousInfinite) {
        queryClient.setQueryData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(infiniteKey, {
          ...previousInfinite,
          pages: previousInfinite.pages.map((page) => ({
            ...page,
            data: page.data.map((item) =>
              item.clientIntegrationModuleId === clientIntegrationModuleId
                ? mergeCampaignJobBoard(item, payload) ?? item
                : item
            ),
          })),
        });
      }

      return { previousDetail, previousList, previousInfinite };
    },
    onError: (_, __, context) => {
      const detailKey = campaignJobBoardQueryKeys.detail(campaignId, clientIntegrationModuleId, clientId);
      const listKey = campaignJobBoardQueryKeys.list(campaignId, clientId);
      const infiniteKey = campaignJobBoardQueryKeys.infinite(campaignId, clientId);

      if (context?.previousDetail) {
        queryClient.setQueryData<CampaignJobBoardType>(detailKey, context.previousDetail);
      }

      if (context?.previousList) {
        queryClient.setQueryData<PagedResponse<CampaignJobBoardType>>(
          listKey,
          context.previousList
        );
      }

      if (context?.previousInfinite) {
        queryClient.setQueryData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(
          infiniteKey,
          context.previousInfinite
        );
      }
    },
    onSuccess: (updatedJobBoard) => {
      const detailKey = campaignJobBoardQueryKeys.detail(campaignId, clientIntegrationModuleId, clientId);
      const listKey = campaignJobBoardQueryKeys.list(campaignId, clientId);
      const infiniteKey = campaignJobBoardQueryKeys.infinite(campaignId, clientId);

      queryClient.setQueryData<CampaignJobBoardType>(detailKey, updatedJobBoard);
      queryClient.setQueryData<PagedResponse<CampaignJobBoardType>>(listKey, (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          data: current.data.map((item) =>
            item.clientIntegrationModuleId === clientIntegrationModuleId ? updatedJobBoard : item
          ),
        };
      });
      queryClient.setQueryData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(infiniteKey, (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          pages: current.pages.map((page) => ({
            ...page,
            data: page.data.map((item) =>
              item.clientIntegrationModuleId === clientIntegrationModuleId ? updatedJobBoard : item
            ),
          })),
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignJobBoardQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.all, exact: false });
      queryClient.invalidateQueries({ queryKey: campaignJobBoardQueryKeys.months.all, exact: false });
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all(), exact: false });
      queryClient.invalidateQueries({
        queryKey: integrationsKeys.all,
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: segmentsQueryKeys.all,
        exact: false,
      });
    },
  });
}
